'use client';

import { useBunja } from 'bunja/react';
import * as m from 'motion/react-m';
import { useTranslations } from 'next-intl';

import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { css } from '@flipster/styled-system/css';
import { consentBunja } from '@flipster/utils/consent/state';

interface CookiePopupProps {
  onClose: () => void;
}

export const CookiePopup = ({ onClose }: CookiePopupProps) => {
  const t = useTranslations();
  const { acceptAllCookies, acceptRequiredCookies } = useBunja(consentBunja);

  const handleAcceptAllCookies = () => {
    acceptAllCookies();
    onClose();
  };
  const handleAcceptRequiredCookies = () => {
    acceptRequiredCookies();
    onClose();
  };

  return (
    <m.div
      key='cookie-popup'
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={css({
        p: 32,
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        rounded: 8,
        pos: 'fixed',
        bottom: 16,
        right: 16,
        left: { base: 16, tablet: 'auto' },
        w: { tablet: 360 },
        minH: { base: 232, tablet: 272 },
        bg: 'background.default.level-3',
        zIndex: 1000,
      })}
    >
      <div>
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
          We use cookies to ensure site security, enhance performance, remember your settings, and support marketing.
          More details can be found in our{' '}
          <IsomorphicLink
            href='/policies/cookies'
            className={css({ textStyle: 'Text/L/Underline', color: 'content.accent.info', cursor: 'pointer' })}
          >
            <FormattedMessage id='Autogen_common.r251126.s_Cookies_Policy' />
          </IsomorphicLink>
        </p>
      </div>
      <div className={css({ display: 'flex', flexDirection: 'column', gap: 8 })}>
        <Button
          hierarchy='primary'
          color='accent'
          size='medium'
          label={t('Autogen_common.r251126.s_Accept_all_cookies')}
          onClick={handleAcceptAllCookies}
        />
        <Button
          hierarchy='secondary'
          color='accent'
          size='medium'
          label={t('Autogen_common.r251126.s_Accept_required_cookies')}
          onClick={handleAcceptRequiredCookies}
        />
      </div>
    </m.div>
  );
};
